import axios from "axios";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import { PARTNER_CONFIG } from "../data.js";
import { CloudUploadOutlined, CheckCircleOutlined, RocketLaunchOutlined, FolderOpenOutlined } from "@mui/icons-material";
import { Box, Button, MenuItem, Select, Typography, FormControl, InputLabel, Stack, Chip } from "@mui/material";
import { GlobalState } from "context/GlobalContext.js";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError.js";

const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

function PartnerFileUpload({ partnerKey, partnerConfig, uploads, onUpload }) {
	if (!partnerKey) return null;

	return (
		<Stack spacing={1.5}>
			{partnerConfig.files.map((fileDef) => {
				const isUploaded = uploads?.[fileDef.key];

				return (
					<Button
						key={fileDef.key}
						component="label"
						fullWidth
						variant="outlined"
						sx={{
							py: 1.5,
							px: 2,
							textTransform: "none",
							borderRadius: 1.5,
							border: isUploaded ? "1px solid #10b981" : "1px dashed #d1d5db",
							background: isUploaded ? "#ecfdf5" : "white",
							transition: "all 0.2s ease",
							"&:hover": {
								border: isUploaded ? "1px solid #059669" : "1px solid #374151",
								boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
							},
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							justifyContent: "flex-start",
						}}>
						{isUploaded ? (
							<CheckCircleOutlined sx={{ color: "#10b981", fontSize: 20 }} />
						) : (
							<CloudUploadOutlined sx={{ color: "#6b7280", fontSize: 20 }} />
						)}
						<Box sx={{ flex: 1, textAlign: "left" }}>
							<Typography
								sx={{
									fontWeight: 500,
									color: isUploaded ? "#065f46" : "#374151",
									fontSize: "0.875rem",
								}}>
								{fileDef.label}
							</Typography>
							<Typography
								variant="caption"
								sx={{
									color: isUploaded ? "#10b981" : "#6b7280",
									fontSize: "0.75rem",
								}}>
								{isUploaded ? "✓ Uploaded" : "Click to upload"}
							</Typography>
						</Box>
						<VisuallyHiddenInput
							type="file"
							accept=".csv,.xlsx"
							onChange={(e) => onUpload(fileDef.key, e.target.files[0])}
						/>
					</Button>
				);
			})}
		</Stack>
	);
}

export default function FileUpload() {
	const { setSnackBarOpen, setSnackBar, snackBar, LoadingOpen, setLoadingOpen } = GlobalState();
	const [selectedPartner, setSelectedPartner] = useState("");
	const [month, setMonth] = useState("");
	const [year, setYear] = useState(new Date().getFullYear());
	const [uploads, setUploads] = useState({});
	const currentYear = new Date().getFullYear();
	const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
	// const

	const handlePartnerChange = (e) => {
		setSelectedPartner(e.target.value);
		setUploads({});
	};

	const handleUpload = (fileType, file) => {
		setUploads((prev) => ({
			...prev,
			[fileType]: file,
		}));
	};

	const canRenderUploads = selectedPartner && month && year;

	const isJobReady = () => {
		if (!canRenderUploads) return false;
		return PARTNER_CONFIG[selectedPartner].files.every((file) => uploads[file.key]);
	};

	const handleCreateJob = async () => {
		try {
			setLoadingOpen(true);
			const formData = new FormData();
			// Standardized date format for 2026 backends
			const effectiveDate = `${year}-${String(month).padStart(2, "0")}-01`;

			formData.append("partner", selectedPartner);
			formData.append("effective_date", effectiveDate);
			formData.append("month", month);
			formData.append("year", year);
			Object.entries(uploads).forEach(([key, file]) => {
				if (file) formData.append(key, file);
			});

			await axios.post("/api/shipping_partner_reco/create_job", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Job created successfully", severity: "success" });
		} catch (err) {
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrMsgFromError(err), severity: "error" });
			console.error("Upload failed", err.response?.data || err);
		} finally {
			// setIsSubmitting(false);pass
		}
	};

	return (
		<Box>
			{/* Compact Header */}
			<Box sx={{ mb: 3 }}>
				<Typography variant="h6" sx={{ fontWeight: 600, color: "#111827", mb: 0.5, fontSize: "1rem" }}>
					File Upload
				</Typography>
				<Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.85rem" }}>
					Select partner and period to upload files
				</Typography>
			</Box>

			{/* Compact Selection Grid */}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
					gap: 1.5,
					mb: 3,
				}}>
				<FormControl size="small" fullWidth>
					<InputLabel sx={{ fontSize: "0.875rem" }}>Partner</InputLabel>
					<Select
						value={selectedPartner}
						label="Partner"
						onChange={handlePartnerChange}
						sx={{ fontSize: "0.875rem", "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#212121" } }}>
						{Object.entries(PARTNER_CONFIG).map(([key, config]) => (
							<MenuItem key={key} value={key} sx={{ fontSize: "0.875rem" }}>
								{config.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<FormControl size="small" fullWidth>
					<InputLabel sx={{ fontSize: "0.875rem" }}>Month</InputLabel>
					<Select
						value={month}
						label="Month"
						onChange={(e) => setMonth(e.target.value)}
						sx={{ fontSize: "0.875rem", "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#212121" } }}>
						{["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, idx) => (
							<MenuItem key={idx} value={idx + 1} sx={{ fontSize: "0.875rem" }}>
								{m}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<FormControl size="small" fullWidth>
					<InputLabel sx={{ fontSize: "0.875rem" }}>Year</InputLabel>
					<Select
						value={year}
						label="Year"
						onChange={(e) => setYear(e.target.value)}
						sx={{ fontSize: "0.875rem", "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#212121" } }}>
						{years.map((y) => (
							<MenuItem key={y} value={y} sx={{ fontSize: "0.875rem" }}>
								{y}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{/* Compact Upload Section */}
			{canRenderUploads && (
				<Box sx={{ mb: 3 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#111827", mb: 2, fontSize: "0.9rem" }}>
						Required Files
					</Typography>
					<Box sx={{ background: "#f9fafb", p: 2, borderRadius: 1.5, border: "1px solid #e5e7eb" }}>
						<PartnerFileUpload
							partnerKey={selectedPartner}
							partnerConfig={PARTNER_CONFIG[selectedPartner]}
							uploads={uploads}
							onUpload={handleUpload}
						/>
					</Box>
				</Box>
			)}

			{/* Compact Action Button */}
			<Button
				variant="contained"
				fullWidth
				startIcon={<RocketLaunchOutlined />}
				sx={{
					py: 1.5,
					fontSize: "0.9rem",
					fontWeight: 600,
					textTransform: "none",
					background: isJobReady() ? "#212121" : "#d1d5db",
					borderRadius: 1.5,
					"&:hover": isJobReady() ? { background: "#374151" } : {},
					"&:disabled": { background: "#d1d5db", color: "#9ca3af" },
				}}
				disabled={!isJobReady()}
				onClick={handleCreateJob}>
				Create Job
			</Button>
		</Box>
	);
}
